import { RoleKind } from '../models/brijjsports.enums';

/**
 * One tenant a user belongs to. A user with two memberships (e.g. coach at
 * Arrow Dynamic + parent at Riverside) can switch tenants without re-auth.
 * Sign-in surfaces a tenant picker when `memberships.length > 1`.
 */
export interface UserMembership {
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  roles: RoleKind[];
  /** Coach / Parent / Athlete record id within this tenant; null for Owner. */
  refId: string | null;
}

/**
 * A mock user account derived from a tenant's seeded data. Every Owner /
 * Coach / Parent / Athlete is a user of their tenant.
 *
 * `role` is the user's *primary* / active role on sign-in.
 * `roles` (optional) lists every role this user can act as inside the
 * *active* tenant. When absent, treat it as `[role]`. The UI role-switcher
 * (topbar) lets the user toggle the active role across `roles` without
 * re-auth.
 *
 * `memberships` (optional, follow-up) lists every tenant the user belongs
 * to. When absent, the user is implicitly a member of the single tenant
 * named by `tenantId`/`tenantSlug`/`tenantName`. The seed catalog today
 * builds one `MockUser` per tenant per role; identity merging across
 * tenants (same email, multiple memberships) is the next-phase work.
 */
export interface MockUser {
  /** Stable id (tenantId + role + refId or 'owner'). */
  id: string;
  email: string;
  password: string;
  name: string;
  role: RoleKind;
  /** Every role this user can adopt within the active tenant. Defaults to `[role]`. */
  roles?: RoleKind[];
  /** Every tenant this user belongs to. Defaults to a single entry built from `tenantId`/`tenantSlug`/`tenantName`/`role`/`refId`. */
  memberships?: UserMembership[];
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  /** Coach / Parent / Athlete record id; null for Owner. */
  refId: string | null;
}

/** All roles this user can adopt — primary `role` plus any additional `roles`. */
export function userRoles(user: MockUser): RoleKind[] {
  if (!user.roles?.length) return [user.role];
  return user.roles.includes(user.role) ? user.roles : [user.role, ...user.roles];
}

/**
 * All tenants this user belongs to. Synthesized from the user's primary
 * tenant fields when `memberships` is absent so callers don't have to
 * branch on shape.
 */
export function userMemberships(user: MockUser): UserMembership[] {
  if (user.memberships?.length) return user.memberships;
  return [
    {
      tenantId: user.tenantId,
      tenantSlug: user.tenantSlug,
      tenantName: user.tenantName,
      roles: userRoles(user),
      refId: user.refId
    }
  ];
}

/** Shared mock password for every demo account. */
export const MOCK_PASSWORD = 'demo1234';
